import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { Channel } from '../types/channel';
import { useSubscribe } from '../hooks/useSubscriptions';

interface SubscribeButtonProps {
  channel: Channel;
  myEmail?: string | null;
  /** Called with the server's count after the viewer subscribes or unsubscribes. */
  onSubscriberCountChange?: (count: number) => void;
}

const SubscribeButton: React.FC<SubscribeButtonProps> = ({ channel, myEmail, onSubscriberCountChange }) => {
  const { t } = useTranslation();
  const { subscribed, loading, subscriberCount, toggle } = useSubscribe(channel, myEmail);

  useEffect(() => {
    if (subscriberCount !== null) onSubscriberCountChange?.(subscriberCount);
  }, [subscriberCount, onSubscriberCountChange]);
  // Confirmation state: when true, shows inline "¿Dejar de seguir?" prompt
  const [confirming, setConfirming] = useState(false);

  if (!myEmail || channel.ownerEmail?.toLowerCase() === myEmail.toLowerCase()) return null;

  // Subscribed state — show compact bell + label, click triggers confirm prompt
  if (subscribed) {
    if (confirming) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', whiteSpace: 'nowrap' }}>
            {t('channel.unsubscribe_confirm')}
          </span>
          <button
            onClick={async () => { await toggle(); setConfirming(false); }}
            disabled={loading}
            style={{
              padding: '4px 10px', borderRadius: 5, border: 'none',
              background: 'var(--theme-error, #dc2626)', color: '#fff',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {t('channel.unsubscribe_yes')}
          </button>
          <button
            onClick={() => setConfirming(false)}
            style={{
              padding: '4px 10px', borderRadius: 5,
              border: '1px solid var(--theme-border, #e5e7eb)',
              background: 'transparent',
              color: 'var(--text-primary, #111)',
              fontSize: 12, fontWeight: 500, cursor: 'pointer',
            }}
          >
            {t('channel.unsubscribe_cancel')}
          </button>
        </div>
      );
    }

    return (
      <button
        onClick={() => setConfirming(true)}
        aria-label={t('channel.unsubscribe_aria')}
        title={t('channel.subscribed')}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '5px 12px', borderRadius: 20,
          border: '1px solid var(--theme-border, #e5e7eb)',
          background: 'var(--surface-hover, #f3f4f6)',
          color: 'var(--text-primary, #111)',
          fontWeight: 500, fontSize: 12,
          cursor: 'pointer',
          transition: 'background 0.15s ease',
        }}
      >
        <Bell size={13} aria-hidden />
        {t('channel.subscribed')}
      </button>
    );
  }

  // Not subscribed — single click to subscribe
  return (
    <button
      onClick={toggle}
      disabled={loading}
      aria-label={t('channel.subscribe_aria')}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '5px 12px', borderRadius: 20,
        border: 'none',
        background: 'var(--theme-primary, #1976d2)',
        color: 'var(--button-primary-foreground, #fff)',
        fontWeight: 600, fontSize: 12,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.7 : 1,
        transition: 'opacity 0.15s ease',
        flexShrink: 0,
      }}
    >
      <Bell size={13} aria-hidden />
      {t('channel.subscribe')}
    </button>
  );
};

export default SubscribeButton;
